(ns app.front.view.search.core
  (:require ["@carbon/icons-react/es/location/16" :default Location16]
            [app.front.dashboard.map :as dashboard-map]
            [app.front.dashboard.search :as search]
            [app.front.search-result :as sr]
            [app.front.ui :as ui]
            [app.front.ui.multi-select :as ui-multi-select]
            [app.front.ui.results-tab-switcher :as ui-rts]
            [app.front.ui.save-search-dialog :as ui-save-search-dialog]
            [app.front.ui.saved-searches-dropdown :as ui-saved-searches-dropdown]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.util :as futil]
            [app.front.view.search.list-view :as list-view]
            [app.front.view.search.mobile :as mobile]
            [app.front.view.search.property :as search-property]
            [app.front.view.search.saved-searches :as saved-searches]
            [app.front.view.search.subs :as search-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.session :as session]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))

(defn search-bar [tr]
  (let [domain-groups          (<sub [::search-subs/domain-groups])
        domain-group-ids       (<sub [::search-subs/domain-group-ids])
        search-term            (<sub [::search-subs/search-term])
        selected-domain-groups (->> domain-groups
                                    (filter #(contains? domain-group-ids (:db/id %)))
                                    set)
        border-styles          "border border-solid border-ui-gray rounded"]
    [:form.flex.flex-col.md:flex-row.w-full.gap-2
     {:on-submit (fn [e]
                   (.preventDefault e)
                   (>evt [::search-subs/perform-search]))}
     (when (seq domain-groups)
       [:div.relative.w-full.md:w-60.flex-none
        (when (or (empty? selected-domain-groups)
                  (= 1 (count domain-groups)))
          [:div.absolute.left-4.top-0.z-10.h-full.flex.items-center.pointer-events-none.text-ui-night-sky-100
           [:> Location16]])
        (if (< 1 (count domain-groups))
          [:div
           {:class border-styles}
           [ui-multi-select/filterable-multi-select
            (p/assoc-if {:id          "search-bar-municipality"
                         :light       true
                         :size        :lg
                         :values      domain-groups
                         :value       selected-domain-groups
                         :val->str    :group/name
                         :placeholder (tr [:search/organization])
                         :label-text  (tr [:search/organization])
                         :hide-label? true
                         :on-change   #(>evt [::search-subs/select-domain-groups (set (map :db/id %))])}
                        :class-name (when (empty? selected-domain-groups)
                                      "[&_input]:!pl-10"))]]
          [:div.h-full.flex.items-center.pl-10.pr-4.bg-white
           {:class border-styles}
           (:group/name (first selected-domain-groups))])])
     [:div.flex-1.min-w-0
      {:class border-styles}
      [ui/search {:id                      "search-bar-input"
                  :light                   true
                  :class-name              "border-b-0"
                  :label-text              (tr [:dashboard-search/search])
                  :placeholder             (tr [:search/global-placeholder])
                  :close-button-label-text (tr [:search/empty])
                  :value                   search-term
                  :on-change               #(rf/dispatch-sync [::search-subs/set-search-term (futil/tval %)])
                  :show-clear?             (ff/enabled? :feature-flag.id/workspace-search-updates)
                  :clear-fn                #(rf/dispatch-sync [::search-subs/set-search-term ""])
                  :hide-label              true}]]
     [ui/button {:type       :submit
                 :size       :field
                 :class-name "w-full md:w-auto flex-none justify-center max-w-none"}
      (tr [:search/search])]]))

(defn save-this-search-button
  [tr]
  (when (ff/enabled? :feature-flag.id/saved-searches)
    [:div.flex.justify-end.mt-2
     [ui/button {:kind       :ghost
                 :size       :small
                 :class-name "font-semibold uppercase"
                 :data-cy    "save-this-search"
                 :disabled   (not (<sub [::search-subs/save-enabled?]))
                 :on-click   #(>evt [::saved-searches/open-save-dialog])}
      (tr [:saved-search/save-this-search])]]))

(defn filter-controls [tr config-loading?]
  (let [filter-values (<sub [::search/filter-values])
        conditions    (concat (<sub [::search/button-filter-conditions])
                              (<sub [::search-subs/selectable-filter-conditions]))]
    [:<>
     [:div.flex.flex-wrap.-mx-2.items-start
      [search/filter-selects tr config-loading?
       {:conditions conditions
        :light?     false}]]
     (when (seq filter-values)
       [ui/button {:kind       :ghost
                   :size       :small
                   :class-name "mt-2 font-semibold uppercase"
                   :on-click   #(>evt [::search/clear-filters])}
        (tr [:clear])])
     [save-this-search-button tr]]))

(defn- workspace-search-panel [tr]
  (let [config-loading? (<sub [::search/config-loading?])]
    [:div.hidden.md:block
     [:h2.mb-4.font-semibold (tr [:search/workspace-results])]
     [:div.border.border-solid.border-ui-gray.rounded.p-4
      [filter-controls tr config-loading?]]]))

(defn- entity-filter-card [tr heading-key]
  (let [config-loading? (<sub [::search/config-loading?])
        filter-values   (<sub [::search/filter-values])]
    [:div.hidden.md:block
     [:h2.mb-4.font-semibold (tr [heading-key])]
     [:div.border.border-solid.border-ui-gray.rounded.p-4
      [:div.flex.flex-wrap.-mx-2.items-start
       [search/filter-selects tr config-loading?
        {:light?     false
         :conditions (<sub [::search-subs/selectable-filter-conditions])}]]
      (when (seq filter-values)
        [ui/button {:kind       :ghost
                    :size       :small
                    :class-name "mt-2 font-semibold uppercase"
                    :on-click   #(>evt [::search/clear-filters])}
         (tr [:clear])])
      [save-this-search-button tr]]]))

(defn- licence-search-panel [tr]
  [entity-filter-card tr :search/licence-results])

(defn- contractor-registration-search-panel [tr]
  [entity-filter-card tr :search/contractor-registration-results])

(defn- complaint-search-panel [tr]
  [entity-filter-card tr :search/complaint-results])

(defn available-results-tabs [map-available?]
  (cond->> ui-rts/default-tabs
    (not map-available?) (remove #(= :dashboard-search/map-view (:id %)))))

(defn- title-results [tr]
  (let [total-count  (<sub [::search/result-total-count])
        in-progress? (<sub [::search/search-in-progress?])]
    [:div.flex
     [:h3.mb-0.font-semibold
      (tr [:dashboard-search/results])
      (when (and total-count
                 (not in-progress?))
        (str " (" total-count ")"))]
     (when in-progress?
       (cc/loading-spinner {:with-overlay false
                            :small        true
                            :class-name   "ml-2 my-1"}))]))

(defn- grid-results [tr search-mode]
  (if (<sub [::search/has-results?])
    [:<>
     [:div.flex.flex-col.sm:flex-row.w-full.justify-end
      {:class (when (<sub [::search-subs/mobile?]) "hidden")}
      [:div
       [sr/search-result-sort {:tr           tr
                               :value        (<sub [::search/sort-condition])
                               :on-change    #(>evt [::search/set-sort-condition %])
                               :sort-options (:sort-options (<sub [::search/search-config]))}]]]
     [:div
      [search/search-results (:render-search-result search-mode)]
      [search/back-to-top-button tr]
      [search/load-more tr]]]
    [search/results-placeholder tr]))

(defn- map-results [tr]
  (when-let [map-center (<sub [::search/map-center])]
    [dashboard-map/map-and-details (util/mmap {:search-opts      (<sub [::search/map-search-opts])
                                               :domain-group-ids (<sub [::search/domain-group-ids])}
                                              tr
                                              map-center)]))

(defn- results-section
  "Results of the selected entity tab, in the view the user picked.

  Unlike the dashboard widget, this page always searches — also for admins — so there is no
  'please do a search' state: an empty result set is just a search that found nothing."
  [tr]
  (let [mobile?     (<sub [::search-subs/mobile?])
        search-mode (<sub [::search/search-mode])
        result-tab  (<sub [::search/result-tab])]
    [:div {:class (if mobile? "mt-2" "mt-10")}
     (when mobile? [mobile/toolbar tr])
     [:div.flex.flex-wrap.justify-between.items-start.gap-4.mb-4
      (when-not mobile? [title-results tr])
      [:div {:class (when mobile? "w-full [&_button]:flex-1 [&_button]:justify-center")}
       [ui-rts/results-tab-switcher
        {:tr           tr
         :tabs         (available-results-tabs (boolean (:map-search-command search-mode)))
         :selected-tab result-tab
         :on-select    #(>evt [::search/set-result-tab %])}]]]
     (case result-tab
       :dashboard-search/map-view  [map-results tr]
       :dashboard-search/list-view [list-view/list-results tr search-mode]
       :dashboard-search/grid-view [grid-results tr search-mode]
       ;; The shared ::result-tab is nil until the search state is initialized: no results yet.
       nil)]))

(defn- content-wrapper [content]
  [:div.bg-ui-white.rounded.p-4.md:p-6
   content])

(defn- search-content [tr selected-entity-tab]
  (if (= :property selected-entity-tab)
    [search-property/panel tr {:save-button [save-this-search-button tr]}]
    [:<>
     (when-not (<sub [::search-subs/mobile?])
       (case selected-entity-tab
         :licence [licence-search-panel tr]
         :contractor-registration [contractor-registration-search-panel tr]
         :complaint [complaint-search-panel tr]
         [workspace-search-panel tr]))
     [results-section tr]]))

(defn search-tabs [tr selected-entity-tab entity-tabs-loading?]
  (let [tab-counts        (<sub [(if (ff/enabled? :feature-flag.id/workspace-search-updates)
                                   ::search-subs/filtered-tab-counts
                                   ::search-subs/tab-counts)])
        visible-tabs      (filter (fn [{:keys [id]}]
                                    (<sub [::search-subs/tab-available? id]))
                                  search-subs/entity-tabs)
        ;; Carbon mounts every panel, so only the selected, available tab gets content.
        selected-visible? (p/find-first visible-tabs {:id selected-entity-tab})]
    (->> visible-tabs
         (mapv (fn [{:keys [id label-key]}]
                 (let [result-count (get tab-counts id)]
                   {:id        id
                    :label     (if (ff/enabled? :feature-flag.id/workspace-search-updates)
                                 (r/as-element
                                   [:span.inline-flex.items-center.gap-1
                                    (tr [label-key])
                                    (when (number? result-count)
                                      [:span.font-semibold {:data-cy (str "search-tab-count-" (name id))}
                                       (str "(" result-count ")")])])
                                 (cond-> (tr [label-key])
                                   result-count (str " (" result-count ")")))
                    :disabled? (and entity-tabs-loading? (not= selected-entity-tab id))
                    :content   (when (and selected-visible? (= id selected-entity-tab))
                                 [content-wrapper [search-content tr id]])}))))))

(defn saved-searches-dropdown-section
  "Flag-gated Saved searches button + dropdown for the page heading row (unified search page
  and dashboard widget). Rows show the saved search's municipalities, term, tab, and filter values."
  [tr]
  (when (ff/enabled? :feature-flag.id/saved-searches)
    (let [rows (<sub [::search-subs/saved-search-rows])]
      [ui-saved-searches-dropdown/saved-searches-dropdown
       {:label        (tr [:saved-search/button-label])
        :searches     rows
        :on-apply     #(>evt [::search-subs/apply-saved-search %])
        :on-remove    #(>evt [::saved-searches/remove-saved-search %])
        :remove-label (tr [:saved-search/remove])
        :empty-text   (tr [:saved-search/empty-list])
        :data-cy      "saved-searches-dropdown"}])))

(defn- save-search-dialog-section
  "One shared save-search dialog for the active tab, closed by the search route's :stop
  controller when the page is left, so an open dialog state cannot leak across routes."
  [tr]
  (when (ff/enabled? :feature-flag.id/saved-searches)
    (let [{:keys [entity-tab search-term domain-group-ids] :as save-data} (<sub [::search-subs/save-data])
          name                                                            (<sub [::saved-searches/save-dialog-name])
          saving?                                                         (<sub [::saved-searches/saving?])]
      [ui-save-search-dialog/save-search-dialog
       {:open?            (<sub [::saved-searches/save-dialog-open?])
        :name             name
        :on-name-change   #(rf/dispatch-sync [::saved-searches/set-save-dialog-name %])
        :on-save          #(>evt [::saved-searches/save-unified-search
                                  (saved-searches/build-save-request
                                    (assoc save-data
                                           :name    name
                                           :subtype (saved-searches/entity-tab->subtype entity-tab)))])
        :on-close         #(>evt [::saved-searches/close-save-dialog])
        :title            (tr [:saved-search/save-this-search])
        :description      (tr [:saved-search/dialog-description])
        :name-label       (tr [:saved-search/name-label])
        :name-placeholder (tr [:saved-search/name-placeholder])
        :preview-text     (saved-searches/preview-text
                            {:domain-group-names (->> (<sub [::search-subs/domain-groups])
                                                      (filter #(contains? domain-group-ids (:db/id %)))
                                                      (map :group/name))
                             :search-term        search-term
                             :tab-label          (tr [(search-subs/tab-title-key entity-tab)])
                             :filter-value-names (saved-searches/preview-filter-names save-data)})
        :save-label       (tr [:saved-search/save-submit])
        :cancel-label     (tr [:cancel])
        :close-label      (tr [:close])
        :saving?          saving?
        :data-cy          "save-search-dialog"}])))

(defn- search-shortcuts [tr mobile?]
  [:div {:class (if mobile? "flex flex-col gap-3 my-3" "flex gap-1 items-center")}
   [:div {:class (when mobile?
                   (str "[&>div]:w-full [&_button[data-cy=saved-searches-dropdown]]:w-full "
                        "[&_button[data-cy=saved-searches-dropdown]]:justify-center "
                        "[&_button[data-cy=saved-searches-dropdown]]:py-3 "
                        "[&_button[data-cy=saved-searches-dropdown]]:!border-2 "
                        "[&_button[data-cy=saved-searches-dropdown]]:border-solid "
                        "[&_button[data-cy=saved-searches-dropdown]]:border-ui-violet-100 "
                        "[&_button[data-cy=saved-searches-dropdown]]:text-ui-violet-100"))}
    [saved-searches-dropdown-section tr]]
   [ui/button {:kind       :ghost                                                       :size :small
               :class-name (if mobile? "w-full max-w-none justify-center uppercase" "")
               :href       (href/href :view/recently-viewed)}
    (tr [:workspace-search/recently-viewed])]])

(defn view [_match]
  (let [tr                   (<sub [:tr])
        mobile?              (<sub [::search-subs/mobile?])
        submitted-term       (<sub [::search-subs/submitted-search-term])
        selected-entity-tab  (<sub [::search-subs/selected-entity-tab])
        session-user         (<sub [:session/user])
        entity-tabs-loading? (<sub [::search-subs/entity-tabs-loading?])
        search-mode          (<sub [::search/search-mode])]
    [:div.cloudpermit-page
     {:class (when mobile? "!px-0 !pt-0 bg-ui-violet-10")}
     [:div {:class (when mobile? "px-4 pt-6 pb-4")}
      [:div.mb-4.flex.items-center.justify-between
       [:h1.font-semibold
        {:class (when mobile? "text-3xl leading-tight mb-6 break-words")}
        (if (and mobile? (not (str/blank? submitted-term)))
          (tr [:search/results-for] [submitted-term])
          (tr [:dashboard-search/search]))]
       (when-not mobile? [search-shortcuts tr false])]
      [search-bar tr]
      (when mobile? [search-shortcuts tr true])]
     (when session-user
       [ui-tabs/tabs
        {:tabs              (search-tabs tr
                                         selected-entity-tab
                                         entity-tabs-loading?)
         :selected-id       selected-entity-tab
         :on-change         #(>evt [::search-subs/select-entity-tab %])
         :aria-label        (tr [:search/entity-tabs])
         :tab-content-class "pt-0 pb-6 px-0"}])
     [save-search-dialog-section tr]
     (when-let [render-overlays (when session-user (:render-overlays search-mode))]
       [render-overlays tr (<sub [:language])])]))

(def route
  {:name        :view/search
   :view        view
   :public?     false
   :parameters  {:query (st/optional-keys {:search-term   s/Str
                                           :selected-tab  (apply s/enum (map (comp name :id)
                                                                             search-subs/entity-tabs))
                                           :complaint-ref s/Str})}
   :controllers [{:parameters {:query [:search-term :selected-tab :complaint-ref]}
                  :start      (fn [{{:keys [search-term selected-tab complaint-ref]} :query}]
                                (when-not (str/blank? search-term)
                                  (>evt [::search-subs/set-search-term search-term]))
                                (when (or selected-tab complaint-ref)
                                  (>evt [::search-subs/set-selected-entity-tab
                                         (if complaint-ref :complaint (keyword selected-tab))]))
                                (when complaint-ref
                                  (>evt [::session/wait-for-user-data
                                         [::search-subs/open-complaint-ref complaint-ref]])))}
                 {:start (fn [_]
                           (>evt [::search-subs/init])
                           (>evt [::saved-searches/fetch-filter-labels])
                           (futil/scroll-to-top))
                  :stop  (fn [_]
                           (>evt [::saved-searches/close-save-dialog])
                           (>evt [::search-subs/cleanup])
                           (>evt [::search-subs/clear-pending-restore]))}]})
